import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardHeader } from '@/app/components/ui/Card';
import { ProgressBar } from '@/app/components/ui/ProgressBar';
import { Skeleton } from '@/app/components/ui/Skeleton';
import type { ParticipationBreakdownRow } from '@/features/participation/api';
import { formatNumber } from '@/lib/format';

/** One of the three breakdown cards (afdeling/locatie/pilotgroep,
 * PLAN-FASE2.md WP-03). `nameOf` resolves a row to a display label — the API
 * only returns ids for department/location, and this WP has no lookup of
 * its own beyond what the caller already fetched for its filters
 * (departments/locations); pilot-group rows carry their own `name` (see
 * `api.ts`), so `nameOf` there just reads `row.name`. A group below
 * `minGroupSize` is shielded rather than shown — the same privacy floor
 * `GET /statistics/participation` already applies to the page as a whole
 * (`min_group_size`), applied per row here since the endpoint doesn't
 * shield individual breakdown rows itself. */
export function BreakdownCard({
  title,
  rows,
  nameOf,
  emptyMessage,
  minGroupSize,
}: {
  title: string;
  rows: ParticipationBreakdownRow[] | undefined;
  nameOf: (row: ParticipationBreakdownRow) => string;
  emptyMessage: string;
  minGroupSize: number;
}) {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader title={title} />
      {rows === undefined ? (
        <Skeleton kind="table" />
      ) : rows.length === 0 ? (
        <p className="text-ink text-sm">{emptyMessage}</p>
      ) : (
        <ul className="flex flex-col gap-4" data-testid="participation-breakdown">
          {rows.map((row, i) => {
            const shielded = row.invited < minGroupSize;
            const pct = row.invited > 0 ? (row.activated / row.invited) * 100 : 0;
            return (
              <li key={i} className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-ink text-sm">{nameOf(row)}</span>
                  {shielded ? (
                    <span className="text-warning inline-flex items-center gap-1 text-xs font-medium">
                      <Lock className="size-3.5" aria-hidden="true" />
                      {t('participation.breakdown.shielded.badge')}
                    </span>
                  ) : (
                    <span className="text-ink text-sm">
                      {t('participation.breakdown.ratio', {
                        activated: formatNumber(row.activated),
                        invited: formatNumber(row.invited),
                        pct: formatNumber(Math.round(pct)),
                      })}
                    </span>
                  )}
                </div>
                {shielded ? (
                  <p className="text-accent text-[13px]">
                    {t('participation.breakdown.shielded.message', {
                      invited: formatNumber(row.invited),
                      min: formatNumber(minGroupSize),
                    })}
                  </p>
                ) : (
                  <ProgressBar pct={pct} />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
